import { Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import { IonRouterOutlet } from '@ionic/angular';

import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { OrderMenuItemSourceType } from '../../../core/enums/order-menu-item-source-type.enum';

import { Address } from '../../../core/models/address.model';
import { GroceryMenuItem } from '../../../core/models/grocery-menu-item.model';
import { GroceryVendorDetails } from '../../../core/models/grocery-vendor-details.model';
import { MultiLanguageValue } from '../../../core/models/multi-language-value.model';
import { NewCartItem } from '../../../core/models/new-cart-item.model';

import { AnalyticsService } from '../../../core/services/analytics.service';
import { CartService } from '../../../core/services/cart.service';
import { ModalService } from '../../../core/services/modal.service';
import { NoticePeriodService } from '../../../core/services/notice-period.service';
import { SettingsService } from '../../../core/services/settings.service';

import { AnalyticsConfig, TOKEN_ANALYTICS_CONFIG } from '../../../core/config/analytics.config';

import {
  GroceryItemDetailsModalComponent,
  GroceryItemDetailsModalPageIdentifier,
  GroceryItemDetailsModalParams,
} from '../../../modals/grocery-item-details-modal/grocery-item-details-modal.component';

import { Helpers } from '../../../core/helpers';

@Component({
  selector: 'app-grocery-item',
  templateUrl: './grocery-item.component.html',
  styleUrls: ['./grocery-item.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GroceryItemComponent implements OnInit, OnDestroy {
  @Input()
  public address: Address;

  @Input()
  public item: GroceryMenuItem;

  @Input()
  public vendorDetails: GroceryVendorDetails;

  public noticePeriodWarning: string;

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private routerOutlet: IonRouterOutlet,
    private cartService: CartService,
    private noticePeriodService: NoticePeriodService,
    private analyticsService: AnalyticsService,
    private modalService: ModalService,
    private settingsService: SettingsService,
    @Inject(TOKEN_ANALYTICS_CONFIG) private analyticsConfig: AnalyticsConfig,
  ) {}

  ngOnInit() {
    this.noticePeriodWarning = this.noticePeriodService.getNoticePeriodWarningMessageForGroceryItem({
      dateTime: this.vendorDetails.soonestAvailableDateTimeSlotIso,
      noticePeriod: this.item.noticePeriodHours,
    });

    // This will both initialize the item quantity and keep it
    // updated as the cart changes
    this.cartService.cart$
      .pipe(
        map(() => this.cartService.getItemQuantityByVendor(this.vendorDetails.vendorId, this.item.menuItemId)),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((newQuantity) => {
        if (this.item.selectedQuantity !== newQuantity) {
          this.item.selectedQuantity = newQuantity;
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public onShowItemDetails(): void {
    const itemDetailsParams: GroceryItemDetailsModalParams = {
      itemDetails: this.item,
      vendorDetails: this.vendorDetails,
      selectedAddress: this.address,
      shouldTrackCleverTapEvents: true,
    };

    void this.modalService.showCard({
      component: GroceryItemDetailsModalComponent,
      id: GroceryItemDetailsModalPageIdentifier,
      setAutoHeightOnBrowser: true,
      presentingElement: this.routerOutlet.nativeEl,
      componentProps: { itemDetailsParams },
    });
  }

  public onMaybeAddItem(): void {
    const hasSelectedMaxQuantity = this.item.selectedQuantity === this.item.quantity;

    if (!this.item.inStock || hasSelectedMaxQuantity) {
      return;
    }

    void this.maybeUpdateMenuItemQuantity(this.item.selectedQuantity ? this.item.selectedQuantity + 1 : 1).then((wasAdded) => {
      if (wasAdded) {
        this.trackItemAddedCleverTapEvent();
      }
    });
  }

  public onRemoveItem(): void {
    if (this.item.selectedQuantity === 0) {
      return;
    }

    void this.maybeUpdateMenuItemQuantity(this.item.selectedQuantity - 1);
  }

  private maybeUpdateMenuItemQuantity(newQuantity: number): Promise<boolean> {
    if (newQuantity === 0) {
      this.cartService.removeItemsFromCart(this.item.vendorId, [this.item.menuItemId]);
      return Promise.resolve(true);
    }

    const newCartItem: NewCartItem = {
      catererId: this.item.vendorId,
      catererName: this.vendorDetails.name,
      minimumOrderValue: this.vendorDetails.minimumOrderValue,
      menuItemId: this.item.menuItemId,
      menuItemName: this.item.name,
      serviceType: this.item.serviceType,
      sourceId: this.item.vendorId,
      sourceType: OrderMenuItemSourceType.Vendor,
      imageUrl: this.item.imageUrl,
      quantity: newQuantity,
      total: Helpers.multiplyWithPrecision(this.item.price, newQuantity),
      noticePeriod: this.item.noticePeriodHours,
    };

    return this.cartService.maybeAddToCart({ item: newCartItem, address: this.address }).then(({ added }) => {
      void this.analyticsService.trackEvent({
        name: this.analyticsConfig.eventName.menuItemDetailsPageAddToCart,
        data: {
          [this.analyticsConfig.propertyName.catererId]: this.item.vendorId,
          [this.analyticsConfig.propertyName.menuItemId]: this.item.menuItemId,
          [this.analyticsConfig.propertyName.amount]: this.item.price,
          [this.analyticsConfig.propertyName.currency]: this.settingsService.currencySymbolEN,
          [this.analyticsConfig.propertyName.isGroceryItem]: true,
        },
        includeAreaDateTime: true,
      });

      return added;
    });
  }

  private trackItemAddedCleverTapEvent(): void {
    const itemName = this.item.nameJson ? (JSON.parse(this.item.nameJson) as MultiLanguageValue) : null;

    this.analyticsService.trackItemAddedCleverTapEvent({
      id: this.item.menuItemId,
      nameEn: itemName ? Helpers.getLocalizedMultiLanguageValue(itemName, 'en') : null,
      nameAr: itemName ? Helpers.getLocalizedMultiLanguageValue(itemName, 'ar') : null,
      currency: this.settingsService.currencySymbolEN,
      amount: this.item.price,
      vendorId: this.item.vendorId,
      serviceType: this.item.serviceType,
    });
  }
}
